import {
  createContext, useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import API_URL from '../config';
import { getLocalDateString } from '../utils/formatters';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');

      if (storedToken) {
        setToken(storedToken);

        // Fetch fresh user data from server
        try {
          const localDate = getLocalDateString();
          const response = await fetch(`${API_URL}/api/users/me?date=${localDate}`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            // Token invalid - clear auth state
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch user data:', error);
          // Fall back to cached data if network fails
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = (userData, authToken) => {
    // Clear daily puzzle localStorage to prevent progress cross-contamination between accounts
    localStorage.removeItem('dailyPuzzleProgress');
    localStorage.removeItem('dailyPuzzle');

    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = useCallback(async () => {
    // Sync in-progress puzzle to server before clearing localStorage
    // This ensures progress is saved when user logs out from any page
    const currentToken = token || localStorage.getItem('token');
    if (currentToken) {
      try {
        const progressStr = localStorage.getItem('dailyPuzzleProgress');
        if (progressStr) {
          const progress = JSON.parse(progressStr);
          // Only sync if puzzle is in progress (has pieces but not complete)
          if (progress.piecePositions && !progress.isComplete) {
            const localDate = getLocalDateString();
            await fetch(`${API_URL}/api/daily/progress?date=${localDate}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${currentToken}`,
              },
              body: JSON.stringify({
                piecePositions: progress.piecePositions,
                canvasWidth: progress.canvasWidth,
                playTime: progress.playTime || 0,
                moves: progress.moves || 0,
                deviceTier: progress.deviceTier,
              }),
            });
          }
        }
      } catch (err) {
        // Silently fail - sync is best-effort before logout
      }
    }

    // Clear daily puzzle localStorage to prevent progress cross-contamination between accounts
    localStorage.removeItem('dailyPuzzleProgress');
    localStorage.removeItem('dailyPuzzle');

    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, [token]);

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshUser = useCallback(async () => {
    if (!token) return null;

    try {
      const localDate = getLocalDateString();
      const response = await fetch(`${API_URL}/api/users/me?date=${localDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh user data:', error);
    }
    return null;
  }, [token]);

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    updateUser,
    refreshUser,
    loading,
  }), [user, token, loading, refreshUser, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
